import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { PCP_OFFICIAL_WHATSAPP, DEFAULT_LANDING_CONTENT, DEFAULT_CATALOG_NOTICE, DEFAULT_CHECKOUT_NOTICE, type LandingContent } from '@/services/finances';
import { DEFAULT_RATE_MATRIX, diffRateMatrix, type ShippingRateMatrix } from '@/services/shipping';
import { recordAudit } from '@/services/audit';
import {
  normalizeHeroColor,
  normalizeHeroFont,
  normalizeHeroShadow,
  normalizeHeroOverlay,
  normalizeHeroTitleSize,
  normalizeSectionTitleSize,
  normalizeTextWeight,
  normalizeSectionShadow,
  normalizeSectionFont,
  normalizeShadowColor,
} from '@/services/heroText';

// Configuración central "Plug & Play" de PCP. Es la única fuente de verdad de
// los parámetros contables, el canal de WhatsApp y el contenido editable del
// Home. La página /admin/configuracion lee y escribe aquí.
// También incluye el contenido editable de la Home (LandingContent).
export interface AppSettings extends LandingContent {
  companyName: string;
  // Logo real de la marca (imagen) para comprobantes y guías de envío.
  companyLogoUrl: string;
  // Datos del remitente que se imprimen junto a los del cliente en la guía.
  senderName: string;
  senderId: string;
  senderPhone: string;
  senderAddress: string;
  senderCity: string;
  distributorDiscountPercent: number;
  sellerCommissionPercent: number;
  donationPercent: number;
  shippingDefaultCost: number;
  contactWhatsapp: string;
  enableOrderEmail: boolean;
  enablePayOnDelivery: boolean;
  enableBankTransfer: boolean;
  enableDonationDog: boolean;
  donationBannerText: string;
  enableWhatsappButton: boolean;
  whatsappBubbleText: string;
  shippingLocalCost: number;
  shippingRegionalCost: number;
  shippingNationalCost: number;
  shippingLocalCities: string;
  shippingRegionalCities: string;
  // Recargo porcentual por pago contraentrega (0 = sin recargo).
  codSurchargePercent: number;
  // Matriz de tarifas de Interrapidísimo por sector (kilo inicial + escalafón).
  shippingRateMatrix: ShippingRateMatrix;
  shippingNationalMetroCities: string;
  shippingDifficultCities: string;
  shippingVeredasCities: string;
  donationFoundationName: string;
  donationFoundationDesc: string;
  donationFoundationUrl: string;
  donationWhatsapp: string;
  donationBankDetails: string;
  donationThankyouText: string;
  // Imagen propia del perrito de donaciones (reemplaza el emoji 🐶).
  donationDogImageUrl: string;
  // Cada cuántos minutos corre el barrido automático de reservas vencidas.
  reservationSweepIntervalMinutes: number;
  // Avisos configurables de la tienda (catálogo Premium y checkout).
  noticeCatalogEnabled: boolean;
  noticeCatalogText: string;
  noticeCheckoutEnabled: boolean;
  noticeCheckoutText: string;
}

const DEFAULTS: AppSettings = {
  ...DEFAULT_LANDING_CONTENT,
  companyName: 'PCP - Paticas con Propósito',
  companyLogoUrl: '',
  senderName: '',
  senderId: '',
  senderPhone: '',
  senderAddress: '',
  senderCity: '',
  distributorDiscountPercent: 37,
  sellerCommissionPercent: 5,
  donationPercent: 2,
  shippingDefaultCost: 0,
  contactWhatsapp: PCP_OFFICIAL_WHATSAPP,
  enableOrderEmail: false,
  enablePayOnDelivery: true,
  enableBankTransfer: true,
  enableDonationDog: true,
  enableWhatsappButton: true,
  whatsappBubbleText: '¿Tienes dudas? ¡Escríbenos!',
  shippingLocalCost: 10000,
  shippingRegionalCost: 16000,
  shippingNationalCost: 22000,
  shippingLocalCities: 'Bogotá',
  shippingRegionalCities:
    'Medellín, Cali, Barranquilla, Bucaramanga, Pereira, Manizales, Cartagena, Ibagué, Cúcuta, Villavicencio, Santa Marta, Pasto, Neiva, Armenia, Valledupar, Montería, Sincelejo, Tunja, Popayán',
  codSurchargePercent: 3,
  shippingRateMatrix: DEFAULT_RATE_MATRIX,
  shippingNationalMetroCities: 'Riohacha, Yopal, Quibdó, Florencia, Arauca, Mocoa, San Andrés',
  shippingDifficultCities: '',
  shippingVeredasCities: '',
  donationBannerText: '¿Quieres ayudar a la Patyfamilia? ¡Dona aquí! 🐾',
  donationFoundationName: 'Patyfamilia',
  donationFoundationDesc: 'Ayúdanos a cuidar y rescatar perros en situación de vulnerabilidad.',
  donationFoundationUrl: '',
  donationWhatsapp: '',
  donationBankDetails: '',
  donationThankyouText: '¡Gracias por ayudarnos! Ya eres parte de la Patyfamilia 🐶❤️',
  donationDogImageUrl: '',
  reservationSweepIntervalMinutes: 15,
  noticeCatalogEnabled: true,
  noticeCatalogText: DEFAULT_CATALOG_NOTICE,
  noticeCheckoutEnabled: true,
  noticeCheckoutText: DEFAULT_CHECKOUT_NOTICE,
};

function mapRow(row: Record<string, any> | null): AppSettings {
  if (!row) return { ...DEFAULTS };
  return {
    companyName: row.company_name ?? DEFAULTS.companyName,
    companyLogoUrl: row.company_logo_url ?? DEFAULTS.companyLogoUrl,
    senderName: row.sender_name ?? DEFAULTS.senderName,
    senderId: row.sender_id ?? DEFAULTS.senderId,
    senderPhone: row.sender_phone ?? DEFAULTS.senderPhone,
    senderAddress: row.sender_address ?? DEFAULTS.senderAddress,
    senderCity: row.sender_city ?? DEFAULTS.senderCity,
    distributorDiscountPercent: Number(
      row.distributor_discount_percent ?? DEFAULTS.distributorDiscountPercent,
    ),
    sellerCommissionPercent: Number(
      row.seller_commission_percent ?? DEFAULTS.sellerCommissionPercent,
    ),
    donationPercent: Number(row.donation_percent ?? DEFAULTS.donationPercent),
    shippingDefaultCost: Number(row.shipping_default_cost ?? DEFAULTS.shippingDefaultCost),
    contactWhatsapp: row.contact_whatsapp ?? DEFAULTS.contactWhatsapp,
    enableOrderEmail: row.enable_order_email ?? DEFAULTS.enableOrderEmail,
    enablePayOnDelivery: row.enable_pay_on_delivery ?? DEFAULTS.enablePayOnDelivery,
    enableBankTransfer: row.enable_bank_transfer ?? DEFAULTS.enableBankTransfer,
    enableDonationDog: row.enable_donation_dog ?? DEFAULTS.enableDonationDog,
    enableWhatsappButton: row.enable_whatsapp_button ?? DEFAULTS.enableWhatsappButton,
    whatsappBubbleText: row.whatsapp_bubble_text ?? DEFAULTS.whatsappBubbleText,
    shippingLocalCost: Number(row.shipping_local_cost ?? DEFAULTS.shippingLocalCost),
    shippingRegionalCost: Number(row.shipping_regional_cost ?? DEFAULTS.shippingRegionalCost),
    shippingNationalCost: Number(row.shipping_national_cost ?? DEFAULTS.shippingNationalCost),
    shippingLocalCities: row.shipping_local_cities ?? DEFAULTS.shippingLocalCities,
    shippingRegionalCities: row.shipping_regional_cities ?? DEFAULTS.shippingRegionalCities,
    codSurchargePercent: Number(row.cod_surcharge_percent ?? DEFAULTS.codSurchargePercent),
    shippingRateMatrix: (row.shipping_rate_matrix ?? DEFAULTS.shippingRateMatrix) as ShippingRateMatrix,
    shippingNationalMetroCities:
      row.shipping_national_metro_cities ?? DEFAULTS.shippingNationalMetroCities,
    shippingDifficultCities: row.shipping_difficult_cities ?? DEFAULTS.shippingDifficultCities,
    shippingVeredasCities: row.shipping_veredas_cities ?? DEFAULTS.shippingVeredasCities,
    donationBannerText: row.donation_banner_text ?? DEFAULTS.donationBannerText,
    donationFoundationName: row.donation_foundation_name ?? DEFAULTS.donationFoundationName,
    donationFoundationDesc: row.donation_foundation_desc ?? DEFAULTS.donationFoundationDesc,
    donationFoundationUrl: row.donation_foundation_url ?? DEFAULTS.donationFoundationUrl,
    donationWhatsapp: row.donation_whatsapp ?? DEFAULTS.donationWhatsapp,
    donationBankDetails: row.donation_bank_details ?? DEFAULTS.donationBankDetails,
    donationThankyouText: row.donation_thankyou_text ?? DEFAULTS.donationThankyouText,
    donationDogImageUrl: row.donation_dog_image_url ?? DEFAULTS.donationDogImageUrl,
    reservationSweepIntervalMinutes: Number(
      row.reservation_sweep_interval_minutes ?? DEFAULTS.reservationSweepIntervalMinutes,
    ),
    noticeCatalogEnabled: row.notice_catalog_enabled ?? DEFAULTS.noticeCatalogEnabled,
    noticeCatalogText: row.notice_catalog_text ?? DEFAULTS.noticeCatalogText,
    noticeCheckoutEnabled: row.notice_checkout_enabled ?? DEFAULTS.noticeCheckoutEnabled,
    noticeCheckoutText: row.notice_checkout_text ?? DEFAULTS.noticeCheckoutText,
    heroTitle: row.hero_title ?? DEFAULTS.heroTitle,
    heroSubtitle: row.hero_subtitle ?? DEFAULTS.heroSubtitle,
    heroMediaType: (row.hero_media_type === 'image' || row.hero_media_type === 'video'
      ? row.hero_media_type
      : 'none') as 'none' | 'image' | 'video',
    heroMediaUrl: row.hero_media_url ?? '',
    heroTextColor: normalizeHeroColor(row.hero_text_color) ?? DEFAULTS.heroTextColor,
    heroTextShadow: normalizeHeroShadow(row.hero_text_shadow),
    heroTextShadowColor: normalizeShadowColor(row.hero_text_shadow_color),
    heroTextFont: normalizeHeroFont(row.hero_text_font),
    heroOverlayIntensity: normalizeHeroOverlay(row.hero_overlay_intensity),
    heroTitleSize: normalizeHeroTitleSize(row.hero_title_size),
    heroTitleWeight: normalizeTextWeight(row.hero_title_weight),
    sectionTextColor: normalizeHeroColor(row.section_text_color) ?? DEFAULTS.sectionTextColor,
    sectionTextShadow: normalizeSectionShadow(row.section_text_shadow),
    sectionTextShadowColor: normalizeShadowColor(row.section_text_shadow_color),
    sectionTextFont: normalizeSectionFont(row.section_text_font),
    sectionTitleSize: normalizeSectionTitleSize(row.section_title_size),
    sectionTitleWeight: normalizeTextWeight(row.section_title_weight),
    referenciasTitle: row.referencias_title ?? DEFAULTS.referenciasTitle,
    referenciasSubtitle: row.referencias_subtitle ?? DEFAULTS.referenciasSubtitle,
    referenciasMediaType: (row.referencias_media_type === 'image' || row.referencias_media_type === 'video'
      ? row.referencias_media_type
      : 'none') as 'none' | 'image' | 'video',
    referenciasMediaUrl: row.referencias_media_url ?? '',
    propositoTitle: row.proposito_title ?? DEFAULTS.propositoTitle,
    propositoText: row.proposito_text ?? DEFAULTS.propositoText,
    propositoMediaType: (row.proposito_media_type === 'image' || row.proposito_media_type === 'video'
      ? row.proposito_media_type
      : 'none') as 'none' | 'image' | 'video',
    propositoMediaUrl: row.proposito_media_url ?? '',
    ctaTitle: row.cta_title ?? DEFAULTS.ctaTitle,
    ctaSubtitle: row.cta_subtitle ?? DEFAULTS.ctaSubtitle,
    footerText: row.footer_text ?? DEFAULTS.footerText,
    contactEmail: row.contact_email ?? DEFAULTS.contactEmail,
    contactCity: row.company_city ?? DEFAULTS.contactCity,
    contactAddress: row.company_address ?? '',
    facebookUrl: row.facebook_url ?? '',
    instagramUrl: row.instagram_url ?? '',
    tiktokUrl: row.tiktok_url ?? '',
  };
}

// ⚠️ SOLO ADMINISTRADOR: esta función lee `select('*')` porque la página
// `/admin/configuracion` (rol Admin) necesita leer Y escribir la configuración
// central. NUNCA la uses en rutas públicas ni para usuarios no-admin; para esos
// casos usa getCompanySettings() / getResendConfig(), que seleccionan solo
// columnas públicas. El acceso real lo cierra RLS con
// has_module_permission('settings', 'read'/'write').
export async function fetchAppSettings(): Promise<AppSettings> {
  if (!isSupabaseConfigured || !supabase) return { ...DEFAULTS };

  const { data, error } = await supabase
    .from('company_settings')
    .select('*')
    .limit(1)
    .maybeSingle();

  if (error || !data) {
    console.warn('[settings] fallback a valores por defecto', error?.message);
    return { ...DEFAULTS };
  }

  return mapRow(data as Record<string, any>);
}

// Guarda los campos editables. Mapea camelCase -> snake_case y hace upsert
// sobre la única fila de configuración central.
export async function saveAppSettings(patch: Partial<AppSettings>): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase no está configurado');
  }

  const snake: Record<string, any> = {};
  if (patch.companyName !== undefined) snake.company_name = patch.companyName;
  if (patch.companyLogoUrl !== undefined) snake.company_logo_url = patch.companyLogoUrl;
  if (patch.senderName !== undefined) snake.sender_name = patch.senderName;
  if (patch.senderId !== undefined) snake.sender_id = patch.senderId;
  if (patch.senderPhone !== undefined) snake.sender_phone = patch.senderPhone;
  if (patch.senderAddress !== undefined) snake.sender_address = patch.senderAddress;
  if (patch.senderCity !== undefined) snake.sender_city = patch.senderCity;
  if (patch.distributorDiscountPercent !== undefined)
    snake.distributor_discount_percent = patch.distributorDiscountPercent;
  if (patch.sellerCommissionPercent !== undefined)
    snake.seller_commission_percent = patch.sellerCommissionPercent;
  if (patch.donationPercent !== undefined) snake.donation_percent = patch.donationPercent;
  if (patch.shippingDefaultCost !== undefined)
    snake.shipping_default_cost = patch.shippingDefaultCost;
  if (patch.contactWhatsapp !== undefined) snake.contact_whatsapp = patch.contactWhatsapp;
  if (patch.enableOrderEmail !== undefined) snake.enable_order_email = patch.enableOrderEmail;
  if (patch.enablePayOnDelivery !== undefined) snake.enable_pay_on_delivery = patch.enablePayOnDelivery;
  if (patch.enableBankTransfer !== undefined) snake.enable_bank_transfer = patch.enableBankTransfer;
  if (patch.enableDonationDog !== undefined) snake.enable_donation_dog = patch.enableDonationDog;
  if (patch.enableWhatsappButton !== undefined) snake.enable_whatsapp_button = patch.enableWhatsappButton;
  if (patch.whatsappBubbleText !== undefined) snake.whatsapp_bubble_text = patch.whatsappBubbleText;
  if (patch.shippingLocalCost !== undefined) snake.shipping_local_cost = patch.shippingLocalCost;
  if (patch.shippingRegionalCost !== undefined) snake.shipping_regional_cost = patch.shippingRegionalCost;
  if (patch.shippingNationalCost !== undefined) snake.shipping_national_cost = patch.shippingNationalCost;
  if (patch.shippingLocalCities !== undefined) snake.shipping_local_cities = patch.shippingLocalCities;
  if (patch.shippingRegionalCities !== undefined) snake.shipping_regional_cities = patch.shippingRegionalCities;
  if (patch.codSurchargePercent !== undefined) snake.cod_surcharge_percent = patch.codSurchargePercent;
  if (patch.shippingRateMatrix !== undefined) snake.shipping_rate_matrix = patch.shippingRateMatrix;
  if (patch.shippingNationalMetroCities !== undefined)
    snake.shipping_national_metro_cities = patch.shippingNationalMetroCities;
  if (patch.shippingDifficultCities !== undefined)
    snake.shipping_difficult_cities = patch.shippingDifficultCities;
  if (patch.shippingVeredasCities !== undefined)
    snake.shipping_veredas_cities = patch.shippingVeredasCities;
  if (patch.donationBannerText !== undefined) snake.donation_banner_text = patch.donationBannerText;
  if (patch.donationFoundationName !== undefined)
    snake.donation_foundation_name = patch.donationFoundationName;
  if (patch.donationFoundationDesc !== undefined)
    snake.donation_foundation_desc = patch.donationFoundationDesc;
  if (patch.donationFoundationUrl !== undefined)
    snake.donation_foundation_url = patch.donationFoundationUrl;
  if (patch.donationWhatsapp !== undefined) snake.donation_whatsapp = patch.donationWhatsapp;
  if (patch.donationBankDetails !== undefined)
    snake.donation_bank_details = patch.donationBankDetails;
  if (patch.donationThankyouText !== undefined)
    snake.donation_thankyou_text = patch.donationThankyouText;
  if (patch.donationDogImageUrl !== undefined)
    snake.donation_dog_image_url = patch.donationDogImageUrl;
  if (patch.noticeCatalogEnabled !== undefined) snake.notice_catalog_enabled = patch.noticeCatalogEnabled;
  if (patch.noticeCatalogText !== undefined) snake.notice_catalog_text = patch.noticeCatalogText;
  if (patch.noticeCheckoutEnabled !== undefined) snake.notice_checkout_enabled = patch.noticeCheckoutEnabled;
  if (patch.noticeCheckoutText !== undefined) snake.notice_checkout_text = patch.noticeCheckoutText;

  if (patch.heroTitle !== undefined) snake.hero_title = patch.heroTitle;
  if (patch.heroSubtitle !== undefined) snake.hero_subtitle = patch.heroSubtitle;
  if (patch.heroMediaType !== undefined) snake.hero_media_type = patch.heroMediaType;
  if (patch.heroMediaUrl !== undefined) snake.hero_media_url = patch.heroMediaUrl;
  if (patch.heroTextColor !== undefined) snake.hero_text_color = patch.heroTextColor;
  if (patch.heroTextShadow !== undefined) snake.hero_text_shadow = patch.heroTextShadow;
  if (patch.heroTextShadowColor !== undefined) snake.hero_text_shadow_color = patch.heroTextShadowColor;
  if (patch.heroTextFont !== undefined) snake.hero_text_font = patch.heroTextFont;
  if (patch.heroOverlayIntensity !== undefined) snake.hero_overlay_intensity = patch.heroOverlayIntensity;
  if (patch.heroTitleSize !== undefined) snake.hero_title_size = patch.heroTitleSize;
  if (patch.heroTitleWeight !== undefined) snake.hero_title_weight = patch.heroTitleWeight;
  if (patch.sectionTextColor !== undefined) snake.section_text_color = patch.sectionTextColor;
  if (patch.sectionTextShadow !== undefined) snake.section_text_shadow = patch.sectionTextShadow;
  if (patch.sectionTextShadowColor !== undefined) snake.section_text_shadow_color = patch.sectionTextShadowColor;
  if (patch.sectionTextFont !== undefined) snake.section_text_font = patch.sectionTextFont;
  if (patch.sectionTitleSize !== undefined) snake.section_title_size = patch.sectionTitleSize;
  if (patch.sectionTitleWeight !== undefined) snake.section_title_weight = patch.sectionTitleWeight;
  if (patch.referenciasTitle !== undefined) snake.referencias_title = patch.referenciasTitle;
  if (patch.referenciasSubtitle !== undefined) snake.referencias_subtitle = patch.referenciasSubtitle;
  if (patch.referenciasMediaType !== undefined) snake.referencias_media_type = patch.referenciasMediaType;
  if (patch.referenciasMediaUrl !== undefined) snake.referencias_media_url = patch.referenciasMediaUrl;
  if (patch.propositoTitle !== undefined) snake.proposito_title = patch.propositoTitle;
  if (patch.propositoText !== undefined) snake.proposito_text = patch.propositoText;
  if (patch.propositoMediaType !== undefined) snake.proposito_media_type = patch.propositoMediaType;
  if (patch.propositoMediaUrl !== undefined) snake.proposito_media_url = patch.propositoMediaUrl;
  if (patch.ctaTitle !== undefined) snake.cta_title = patch.ctaTitle;
  if (patch.ctaSubtitle !== undefined) snake.cta_subtitle = patch.ctaSubtitle;
  if (patch.footerText !== undefined) snake.footer_text = patch.footerText;
  if (patch.contactEmail !== undefined) snake.contact_email = patch.contactEmail;
  if (patch.contactCity !== undefined) snake.company_city = patch.contactCity;
  if (patch.contactAddress !== undefined) snake.company_address = patch.contactAddress;
  if (patch.facebookUrl !== undefined) snake.facebook_url = patch.facebookUrl;
  if (patch.instagramUrl !== undefined) snake.instagram_url = patch.instagramUrl;
  if (patch.tiktokUrl !== undefined) snake.tiktok_url = patch.tiktokUrl;

  const { data: existing } = await supabase
    .from('company_settings')
    .select(
      'id, shipping_rate_matrix, shipping_local_cities, shipping_regional_cities, shipping_national_metro_cities, shipping_difficult_cities, shipping_veredas_cities',
    )
    .limit(1)
    .maybeSingle();

  let entityId: string | null = existing?.id ?? null;
  // Matriz previa capturada antes de escribir, para poder firmar el cambio
  // (antes → después) en la auditoría de tarifas de Interrapidísimo.
  const previousRateMatrix = (existing?.shipping_rate_matrix ?? null) as ShippingRateMatrix | null;

  if (existing?.id) {
    const { error } = await supabase.from('company_settings').update(snake).eq('id', existing.id);
    if (error) throw new Error(error.message);
  } else {
    const { data: inserted, error } = await supabase
      .from('company_settings')
      .insert(snake)
      .select('id')
      .single();
    if (error) throw new Error(error.message);
    entityId = inserted?.id ?? null;
  }

  if (entityId) {
    // Cuando se edita la matriz de tarifas de Interrapidísimo, se firma un
    // registro dedicado (quién y cuándo) con el detalle antes → después de los
    // sectores modificados, en lugar del genérico de configuración.
    const touchesShippingRates =
      snake.shipping_rate_matrix !== undefined ||
      snake.shipping_local_cities !== undefined ||
      snake.shipping_regional_cities !== undefined ||
      snake.shipping_national_metro_cities !== undefined ||
      snake.shipping_difficult_cities !== undefined ||
      snake.shipping_veredas_cities !== undefined;

    if (touchesShippingRates) {
      const diff = patch.shippingRateMatrix
        ? diffRateMatrix(previousRateMatrix, patch.shippingRateMatrix)
        : null;
      const cityChanged =
        (snake.shipping_local_cities !== undefined &&
          snake.shipping_local_cities !== (existing?.shipping_local_cities ?? '')) ||
        (snake.shipping_regional_cities !== undefined &&
          snake.shipping_regional_cities !== (existing?.shipping_regional_cities ?? '')) ||
        (snake.shipping_national_metro_cities !== undefined &&
          snake.shipping_national_metro_cities !== (existing?.shipping_national_metro_cities ?? '')) ||
        (snake.shipping_difficult_cities !== undefined &&
          snake.shipping_difficult_cities !== (existing?.shipping_difficult_cities ?? '')) ||
        (snake.shipping_veredas_cities !== undefined &&
          snake.shipping_veredas_cities !== (existing?.shipping_veredas_cities ?? ''));

      const newParts: string[] = [];
      if (diff) newParts.push(diff.newValue);
      if (cityChanged) newParts.push('Listas de ciudades por sector actualizadas');

      recordAudit({
        entityType: 'shipping_rates',
        entityId,
        action: 'UPDATE',
        fieldName: 'Matriz de tarifas Interrapidísimo',
        oldValue: diff?.oldValue ?? null,
        newValue: newParts.join(' · ') || 'Tarifas de envío actualizadas',
      });
    } else {
      recordAudit({
        entityType: 'settings',
        entityId,
        action: 'UPDATE',
        newValue: Object.keys(snake).join(', '),
      });
    }
  }
}

// Cambia cada cuántos minutos corre el barrido automático de reservas vencidas
// y reprograma el cron en la base mediante un RPC seguro (solo Admin o usuarios
// con permiso de escritura sobre Configuración). Devuelve el intervalo aplicado.
export async function setReservationSweepInterval(
  minutes: number,
): Promise<{ intervalMinutes: number; error: string | null }> {
  if (!isSupabaseConfigured || !supabase) {
    return { intervalMinutes: minutes, error: 'Supabase no está configurado.' };
  }
  try {
    const { data, error } = await supabase.rpc('set_reservation_sweep_interval', {
      p_minutes: minutes,
    });
    if (error) return { intervalMinutes: minutes, error: error.message };
    const value = Number((data as { intervalMinutes?: number } | null)?.intervalMinutes ?? minutes);
    return { intervalMinutes: value, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Error al guardar el intervalo.';
    return { intervalMinutes: minutes, error: message };
  }
}